import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
    const [opts, setOpts] = useState(null);
    const resolverRef = useRef(null);

    const confirm = useCallback((options) => {
        return new Promise((resolve) => {
            resolverRef.current = resolve;
            setOpts({
                confirmText: "Oui",
                cancelText: "Non",
                tone: "warning",
                title: "Attention !",
                ...options,
            });
        });
    }, []);

    const close = (value) => {
        if (resolverRef.current) {
            resolverRef.current(value);
            resolverRef.current = null;
        }
        setOpts(null);
    };

    const onKeyDown = (e) => {
        if (e.key === "Escape") close(false);
        if (e.key === "Enter") close(true);
    };

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {opts &&
                createPortal(
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center"
                        aria-modal="true"
                        role="dialog"
                        aria-labelledby="confirm-title"
                        onKeyDown={onKeyDown}
                    >
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-black/40"
                            onClick={() => close(false)}
                        />

                        {/* Dialog */}
                        <div className="relative mx-4 w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-black/5">
                            <div className="p-5">
                                <div className="flex items-start gap-3">
                                    <div
                                        className={
                                            opts.tone === "warning"
                                                ? "mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100"
                                                : "mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100"
                                        }
                                    >
                                        <span
                                            className={
                                                opts.tone === "warning"
                                                    ? "text-amber-600"
                                                    : "text-gray-500"
                                            }
                                        >
                                            ⚠️
                                        </span>
                                    </div>
                                    <div className="flex-1">
                                        <h2
                                            id="confirm-title"
                                            className="text-lg font-semibold text-gray-900"
                                        >
                                            {opts.title}
                                        </h2>
                                        <div className="mt-2 text-sm text-gray-600">
                                            {opts.message}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-2 border-t p-4">
                                <button
                                    className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                                    onClick={() => close(false)}
                                    autoFocus
                                >
                                    {opts.cancelText}
                                </button>
                                <button
                                    className="rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700"
                                    onClick={() => close(true)}
                                >
                                    {opts.confirmText}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
    return ctx;
};